import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '../utils/toast';
import { Clock, CheckCircle, XCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TakeAssessmentPage = () => {
  const { questionnaireId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    startAssessment();
  }, [questionnaireId]);

  // Timer countdown
  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0) {
      toast.error('Time is up! Auto-submitting your assessment...');
      handleSubmit();
    }
  }, [timeRemaining]);

  const startAssessment = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assessments/start/${questionnaireId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Transform the data to flatten the structure
      const assessmentData = {
        ...response.data.questionnaire,
        questions: response.data.questions
      };
      
      setAssessment(assessmentData);
      setStartTime(Date.now());
      
      // Initialize timer if time limit exists
      if (assessmentData.time_limit_minutes) {
        setTimeRemaining(assessmentData.time_limit_minutes * 60);
      }
      
      // Initialize answers object
      const initialAnswers = {};
      response.data.questions.forEach(q => {
        if (q.question_type === 'mcq_single') {
          initialAnswers[q.question_id] = null;
        } else if (q.question_type === 'mcq_multiple') {
          initialAnswers[q.question_id] = [];
        } else if (q.question_type === 'ranking' || q.question_type === 'arrange') {
          initialAnswers[q.question_id] = [];
        } else if (q.question_type === 'matching') {
          initialAnswers[q.question_id] = {};
        }
      });
      setAnswers(initialAnswers);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start assessment');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleMCQSingleChange = (questionId, optionId) => {
    setAnswers({ ...answers, [questionId]: optionId });
  };

  const handleMCQMultipleChange = (questionId, optionId, checked) => {
    const currentAnswers = answers[questionId] || [];
    if (checked) {
      setAnswers({ ...answers, [questionId]: [...currentAnswers, optionId] });
    } else {
      setAnswers({ ...answers, [questionId]: currentAnswers.filter(id => id !== optionId) });
    }
  };

  const handleRankingChange = (questionId, options) => {
    setAnswers({ ...answers, [questionId]: options.map(o => o.option_id) });
  };

  const moveRankingItem = (questionId, currentIndex, direction) => {
    const question = assessment.questions.find(q => q.question_id === questionId);
    const currentOrder = answers[questionId] || question.options.map(o => o.option_id);
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= currentOrder.length) return;
    
    const newOrder = [...currentOrder];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    
    setAnswers({ ...answers, [questionId]: newOrder });
  };

  const handleMatchingChange = (questionId, leftOptionId, rightOptionId) => {
    const currentMatches = answers[questionId] || {};
    setAnswers({ 
      ...answers, 
      [questionId]: { ...currentMatches, [leftOptionId]: rightOptionId } 
    });
  };

  const handleSubmit = async () => {
    // Validate all questions answered
    const unanswered = assessment.questions.filter(q => {
      const answer = answers[q.question_id];
      if (q.question_type === 'mcq_single') {
        return !answer;
      } else if (q.question_type === 'mcq_multiple') {
        return !answer || answer.length === 0;
      } else if (q.question_type === 'ranking' || q.question_type === 'arrange') {
        return !answer || answer.length === 0;
      } else if (q.question_type === 'matching') {
        return !answer || Object.keys(answer).length === 0;
      } else if (q.question_type === 'drag_drop') {
        return !answer || answer.length === 0;
      }
      return false;
    });

    if (unanswered.length > 0) {
      toast.error(`Please answer all questions. ${unanswered.length} question(s) remaining.`);
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const timeTaken = Math.floor((Date.now() - startTime) / 1000);
      
      // Transform answers to backend format
      const formattedAnswers = assessment.questions.map(q => {
        const answer = answers[q.question_id];
        
        if (q.question_type === 'mcq_single') {
          return {
            question_id: q.question_id,
            question_type: q.question_type,
            selected_options: [answer]
          };
        } else if (q.question_type === 'mcq_multiple') {
          return {
            question_id: q.question_id,
            question_type: q.question_type,
            selected_options: answer
          };
        } else if (q.question_type === 'ranking' || q.question_type === 'arrange') {
          return {
            question_id: q.question_id,
            question_type: q.question_type,
            arranged_order: answer
          };
        } else if (q.question_type === 'matching') {
          return {
            question_id: q.question_id,
            question_type: q.question_type,
            matches: answer
          };
        }
      });

      const response = await axios.post(`${API}/assessments/submit`, {
        questionnaire_id: questionnaireId,
        answers: formattedAnswers,
        time_taken_seconds: timeTaken
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = response.data;
      
      // Show result
      if (result.passed) {
        toast.success(`Congratulations! You passed with ${result.percentage}%!`);
      } else {
        toast.error(`You scored ${result.percentage}%. Pass score is ${assessment.pass_score_percentage}%`);
      }

      // Navigate to results page or back
      setTimeout(() => {
        navigate(-1);
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getAnsweredCount = () => {
    return Object.values(answers).filter(a => {
      if (Array.isArray(a)) return a.length > 0;
      if (typeof a === 'object' && a !== null) return Object.keys(a).length > 0;
      return a !== null && a !== undefined && a !== '';
    }).length;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading assessment...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!assessment) return null;

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header with timer */}
        <div className="sticky top-0 z-10 bg-white border-b shadow-md p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-[22px] font-bold text-gray-900">{assessment.title}</h1>
              <p className="text-sm text-gray-600 mt-1">
                Questions: {getAnsweredCount()}/{assessment.total_questions} answered
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {timeRemaining !== null && (
                <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                  timeRemaining < 300 ? 'bg-red-100 text-red-800' : 
                  timeRemaining < 600 ? 'bg-yellow-100 text-yellow-800' : 
                  'bg-blue-100 text-blue-800'
                }`}>
                  <Clock className="w-5 h-5" />
                  <span className="font-bold text-lg">{formatTime(timeRemaining)}</span>
                </div>
              )}
              <Badge className="bg-purple-100 text-purple-800">
                Pass Score: {assessment.pass_score_percentage}%
              </Badge>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-6 mb-6">
          {assessment.questions.map((question, index) => (
            <Card key={question.question_id} className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <Badge className="bg-blue-100 text-blue-800">
                      Question {index + 1}
                    </Badge>
                    <Badge className={
                      question.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                      question.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }>
                      {question.difficulty.toUpperCase()}
                    </Badge>
                    <span className="text-sm text-gray-600">{question.points} point(s)</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{question.question_text}</h3>
                  {question.hint && (
                    <div className="mt-2 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                      <p className="text-sm text-yellow-800">💡 Hint: {question.hint}</p>
                    </div>
                  )}
                </div>
                {answers[question.question_id] && (
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                )}
              </div>

              {/* MCQ Single Choice */}
              {question.question_type === 'mcq_single' && (
                <div className="space-y-3 mt-4">
                  {question.options.map((option) => (
                    <div
                      key={option.option_id}
                      onClick={() => handleMCQSingleChange(question.question_id, option.option_id)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        answers[question.question_id] === option.option_id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          answers[question.question_id] === option.option_id
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}>
                          {answers[question.question_id] === option.option_id && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        <span className="text-gray-900">{option.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* MCQ Multiple Choice */}
              {question.question_type === 'mcq_multiple' && (
                <div className="space-y-3 mt-4">
                  <p className="text-sm text-gray-600 mb-3">Select all that apply:</p>
                  {question.options.map((option) => (
                    <div
                      key={option.option_id}
                      className={`p-4 border-2 rounded-lg transition-all ${
                        (answers[question.question_id] || []).includes(option.option_id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          checked={(answers[question.question_id] || []).includes(option.option_id)}
                          onCheckedChange={(checked) => 
                            handleMCQMultipleChange(question.question_id, option.option_id, checked)
                          }
                        />
                        <label className="text-gray-900 cursor-pointer flex-1">
                          {option.text}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Ranking and Arrange Questions */}
              {(question.question_type === 'ranking' || question.question_type === 'arrange') && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-3">
                    {question.question_type === 'ranking' ? 'Rank the following items in order (1 = highest):' : 'Arrange the following items in the correct order:'}
                  </p>
                  <div className="space-y-2">
                    {(answers[question.question_id] && answers[question.question_id].length > 0
                      ? answers[question.question_id].map(optionId => 
                          question.options.find(o => o.option_id === optionId)
                        )
                      : question.options
                    ).map((option, index) => (
                      <div 
                        key={option.option_id}
                        className="flex items-center space-x-3 p-4 bg-white border-2 border-gray-200 rounded-lg"
                      >
                        <div className="flex flex-col space-y-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => moveRankingItem(question.question_id, index, 'up')}
                            disabled={index === 0}
                            className="h-6 w-6 p-0"
                          >
                            ▲
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => moveRankingItem(question.question_id, index, 'down')}
                            disabled={index === (answers[question.question_id]?.length || question.options.length) - 1}
                            className="h-6 w-6 p-0"
                          >
                            ▼
                          </Button>
                        </div>
                        <div className="flex items-center space-x-3 flex-1">
                          <Badge className="bg-blue-100 text-blue-800 text-lg font-bold w-8 h-8 flex items-center justify-center">
                            {index + 1}
                          </Badge>
                          <span className="text-gray-900">{option.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Drag-Drop Questions */}
              {question.question_type === 'drag_drop' && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-3">Drag and drop items to organize them:</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-3 text-gray-700">Available Items</h4>
                      <div className="space-y-2">
                        {question.options.map((option) => (
                          <div 
                            key={option.option_id}
                            className="p-3 bg-white border-2 border-gray-200 rounded-lg cursor-move hover:border-blue-400 transition-colors"
                          >
                            {option.text}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold mb-3 text-gray-700">Target Area</h4>
                      <div className="min-h-[200px] border-2 border-dashed border-blue-300 rounded-lg p-3">
                        <p className="text-sm text-gray-500 text-center">Drop items here</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-xs text-yellow-800">
                      💡 Use the ranking controls above to order items, or manually select from the dropdown.
                    </p>
                  </div>
                </div>
              )}

              {/* Matching Questions */}
              {question.question_type === 'matching' && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-3">Match the items on the left with the correct items on the right:</p>
                  <div className="space-y-3">
                    {question.options.slice(0, Math.ceil(question.options.length / 2)).map((leftOption) => {
                      const rightOptions = question.options.slice(Math.ceil(question.options.length / 2));
                      const selectedMatch = answers[question.question_id]?.[leftOption.option_id];
                      
                      return (
                        <div key={leftOption.option_id} className="flex items-center space-x-3">
                          <div className="flex-1 p-3 bg-blue-50 border-2 border-blue-200 rounded-lg">
                            {leftOption.text}
                          </div>
                          <span className="text-gray-400 font-bold">↔</span>
                          <select
                            value={selectedMatch || ''}
                            onChange={(e) => handleMatchingChange(question.question_id, leftOption.option_id, e.target.value)}
                            className="flex-1 p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                          >
                            <option value="">Select match...</option>
                            {rightOptions.map((rightOption) => (
                              <option key={rightOption.option_id} value={rightOption.option_id}>
                                {rightOption.text}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>

        {/* Submit Subchapter */}
        <Card className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 sticky bottom-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Ready to submit?</h3>
              <p className="text-sm text-gray-600 mt-1">
                {getAnsweredCount() === assessment.total_questions 
                  ? '✓ All questions answered' 
                  : `${assessment.total_questions - getAnsweredCount()} question(s) remaining`}
              </p>
            </div>
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                onClick={() => navigate(-1)}
                disabled={submitting}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={submitting || getAnsweredCount() < assessment.total_questions}
                className="bg-purple-600 hover:bg-purple-700 text-white px-8"
              >
                {submitting ? 'Submitting...' : 'Submit Assessment'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default TakeAssessmentPage;
